// Shared v2-styled scaffolding for API Security pages — mirrors the
// Bot/DDoS Config look (Stack spacing={6} sx={{ p: 6 }}, RefreshButton
// in the toolbar, Helmet title, content wrapped in a panel with neutral
// border + rounded corners). Site scope comes from the header selector.
import React from "react";
import { Helmet } from "react-helmet-async";
import { Stack, Typography, Box, Button } from "@mui/material";

import { Lock, Mail } from "lucide-react";

import colors from "../../../../../utils/colors";
import useSiteScope from "../../../../../hooks/user/useSiteScope";
import { RefreshButton } from "../../../../common/Button";
import V2PageHeader from "../../../../common/V2PageHeader";
import { SiteScopeMessage } from "../../../../common/SiteScopeGate";
import SectionHelp from "./components/SectionHelp";
import useAsecPermissions from "./useAsecPermissions";
import { useEffectiveFeature } from "../../../../../contexts/user/EffectiveFeaturesContext";

export function useActiveSite() {
  const scope = useSiteScope({ mode: "single" });
  return {
    siteId: scope.siteUid,
    siteList: scope.siteList,
    selectedSite: scope.selectedSite,
    effectiveSiteId: scope.effectiveSiteId,
    siteScopeLoading: scope.siteScopeLoading,
    siteScopeBlocked: scope.siteScopeBlocked,
  };
}

// v2-styled panel wrapping content. Mirrors the rounded box used in Bot
// Config: white bg, 1px neutral[200] border, p:6, borderRadius 8px.
export function AsecPanel({ children, sx, maxWidth = 1080 }) {
  return (
    <Box sx={{
      borderRadius: "8px",
      border: `solid 1px ${colors.neutral[200]}`,
      backgroundColor: "white",
      p: 6,
      maxWidth,
      width: "100%",
      ...sx,
    }}>
      {children}
    </Box>
  );
}

// Section heading inside an AsecPanel.
export function SectionTitle({ children, mb = 4 }) {
  return (
    <Typography sx={{
fontSize: 16, lineHeight: "24px",
      fontWeight: "medium", color: colors.neutral[1000], mb,
    }}>
      {children}
    </Typography>
  );
}

// Subtle subhead/body label.
export function SubLabel({ children }) {
  return (
    <Typography sx={{
fontSize: 14, lineHeight: "21px",
      color: colors.neutral[700],
    }}>
      {children}
    </Typography>
  );
}

// Top-of-page status banner (Off / Log only / Enforce). Renders only if
// `statusMode` is provided. `tone` controls colour: "ok"/"warn"/"info".
export function StatusBanner({ title, statusMode, tone = "info", action }) {
  const palette = {
    ok:   { bg: "rgba(16, 185, 129, 0.08)", fg: colors.semantic?.success || "#0f766e" },
    warn: { bg: "rgba(245, 158, 11, 0.08)", fg: "#b45309" },
    info: { bg: "rgba(99, 113, 247, 0.08)", fg: colors.primary[500] || "#4338ca" },
  }[tone] || { bg: "rgba(99, 113, 247, 0.08)", fg: colors.primary[500] };
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between"
      sx={{ px: 4, py: 3, borderRadius: "8px", backgroundColor: palette.bg }}>
      <Stack>
        <Typography sx={ { fontSize: 14, color: palette.fg, fontWeight: "medium" }}>
          {title}
        </Typography>
        {statusMode && (
          <Typography sx={ { fontSize: 12, color: colors.neutral[700], mt: 0.5 }}>
            Current mode: <strong>{String(statusMode).replace(/_/g, " ")}</strong>
          </Typography>
        )}
      </Stack>
      {action}
    </Stack>
  );
}

// Licensing-v2 — in-page "Feature not licensed" panel. Rendered when a page
// passes a `featureKey` prop and `useEffectiveFeature(featureKey) === false`.
// Matches the existing v2 panel chrome (white bg, neutral border, p:6 rounded
// corners) so the page still looks like a real page instead of a chrome-less
// blank canvas. Mirrors AccessDenied's two-line layout but with copy that
// reflects licensing (rather than missing RBAC permissions).
// Human-readable display names for the api.* feature keys, so the upsell
// title speaks the feature name an operator recognises (rather than the
// licensing key). Keep in sync with the data-plane gate keys in pingora
// src/licensing.rs.
const FEATURE_DISPLAY_NAMES = {
  "api.openapi_validation": "API Schema Validation",
  "api.dlp": "Data Loss Prevention",
  "api.graphql": "GraphQL Controls",
  "api.schema_drift": "Schema Drift Detection",
  "api.posture_scanning": "Posture Scanning",
  "api.anomaly_engine": "Anomaly Detection",
  "api.auth_primitives": "Authentication",
  "api.siem": "SIEM Integration",
};

function FeatureNotLicensed({ featureKey }) {
  const displayName = FEATURE_DISPLAY_NAMES[featureKey] || "This API Security capability";
  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: `solid 1px ${colors.neutral[200]}`,
        backgroundColor: "white",
        p: 6,
      }}
    >
      <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ minHeight: "360px" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: colors.primary["alpha"],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Lock size={32} color={colors.primary[300]} strokeWidth={2} />
        </Box>
        <Stack spacing={1.5} alignItems="center">
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 500,
              color: colors.neutral[1000],
              textAlign: "center",
            }}
          >
            {displayName} isn&apos;t included in your plan
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: colors.neutral[700],
              textAlign: "center",
              maxWidth: 500,
              lineHeight: "22px",
            }}
          >
            API Security adds runtime protection for your APIs &mdash; schema validation, DLP,
            GraphQL hardening, posture scanning, anomaly detection, and SIEM integration.
            Contact your account team to enable {displayName.toLowerCase()}.
          </Typography>
        </Stack>
        <Button
          component="a"
          href="mailto:sales@layerd.com?subject=API%20Security%20upgrade%20request"
          startIcon={<Mail size={18} />}
          sx={{
            backgroundColor: colors.primary[300],
            color: "white",
            fontSize: 14,
            fontWeight: 500,
            px: 3,
            py: 1.25,
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: colors.primary[400], boxShadow: "none" },
          }}
        >
          Contact account team
        </Button>
        {featureKey ? (
          <Typography
            sx={{
              fontSize: 12,
              color: colors.neutral[500],
              textAlign: "center",
              mt: 1,
            }}
          >
            Reference:{" "}
            <Box
              component="code"
              sx={{
                color: colors.neutral[700],
                backgroundColor: colors.neutral[100],
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
              }}
            >
              {featureKey}
            </Box>
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

// Default page wrapper. Provides title + refresh + content area.
// children is a render-prop receiving { siteId, selectedSite }.
//
// Single-header contract (LDCP-160): every API Security page must use
// exactly one V2PageHeader — rendered here via title / pageDescription /
// headerActions / onRefresh. Do not add PageHead or a second V2PageHeader
// inside children. Use showPageTitle={false} only when the title is passed
// via another prop pattern; still supply pageDescription and headerActions
// here so the shell header is never empty.
export default function AsecPage({
  title,
  helmetTitle,
  onRefresh,
  children,
  banner,
  showPageTitle = true,
  helpTooltip,
  headerActions,
  pageDescription,
  descriptionSx,
  featureKey,
}) {
  const { siteUid: siteId, siteList, selectedSite, status } = useSiteScope({ mode: "single" });
  const { canRead } = useAsecPermissions();
  // Licensing-v2 gate — `undefined` means "still loading effective features"
  // (don't flash the upsell); `false` means the org doesn't have the SKU.
  // When featureKey is not provided the gate is implicitly open (legacy
  // call sites + pages without a 1:1 feature mapping such as Overview/Events).
  const featureAllowed = useEffectiveFeature(featureKey);
  const featureGateLoading = featureKey ? featureAllowed === undefined : false;
  const featureGateBlocked = featureKey ? featureAllowed === false : false;
  const [refreshing, setRefreshing] = React.useState(false);

  const refresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh(siteId);
    } finally {
      setRefreshing(false);
    }
  };

  const displayTitle = (title || "").replace(/^API Security —\s*/i, "").replace(/^API Security ·\s*/i, "");
  const refreshControl = onRefresh ? (
    <RefreshButton onClick={refresh} disabled={refreshing} loading={refreshing} />
  ) : null;
  const headerActionSlot = (refreshControl || headerActions) ? (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
      {refreshControl}
      {headerActions}
    </Stack>
  ) : null;
  const showHeader = Boolean(
    pageDescription ||
    headerActionSlot ||
    (showPageTitle && displayTitle)
  );

  return (
    <>
      <Helmet title={helmetTitle || title} />
      <Stack spacing={6} sx={{ p: 6 }}>
        {showHeader && (
          <V2PageHeader
            title={displayTitle}
            description={pageDescription}
            descriptionSx={{ maxWidth: "100%", ...descriptionSx }}
            sx={{ pb: 0, alignItems: "center" }}
            actions={
              (helpTooltip || headerActionSlot) ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                  {helpTooltip && showPageTitle ? <SectionHelp tooltip={helpTooltip} /> : null}
                  {headerActionSlot}
                </Stack>
              ) : null
            }
          />
        )}

        {banner && banner({ siteId, selectedSite })}

        {status === "loading" || featureGateLoading ? (
          <SiteScopeMessage status="loading" />
        ) : featureGateBlocked ? (
          <FeatureNotLicensed featureKey={featureKey} />
        ) : status === "no_sites" ? (
          <SiteScopeMessage status="no_sites" />
        ) : status === "no_site_selected" ? (
          <SiteScopeMessage status="no_site_selected" />
        ) : !canRead ? (
            <SubLabel>You don&apos;t have permission to view API Security. Ask a project admin for the api_security:read permission.</SubLabel>
          ) : !siteId ? null
          : children(siteId, { selectedSite, siteSiteId: selectedSite?.site_id || siteId })}
      </Stack>
    </>
  );
}
